import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X, MapPin, ChevronDown, Search } from "lucide-react";
import { useBrivioLocation } from "./kit";
import { useAppAuth } from "@/context/AppAuthContext";
import { cityStateFromZip } from "@/lib/geo";

const NAV = [
  { label: "How It Works", to: "/how-it-works" },
  { label: "Services", to: "/search" },
  { label: "For Pros", to: "/providers" },
  { label: "About Us", to: "/about" },
];

const LocationPicker = ({ compact }) => {
  const [loc, setLoc] = useBrivioLocation();
  const [open, setOpen] = useState(false);
  const [zip, setZip] = useState("");
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const apply = (e) => {
    e.preventDefault();
    const m = String(zip).match(/\b(\d{5})\b/);
    if (!m) return;
    const city = cityStateFromZip(m[1]) || null;
    setLoc({ zip: m[1], city });
    setOpen(false); setZip("");
  };

  const label = loc?.city || (loc?.zip ? `ZIP ${loc.zip}` : "Set location");

  return (
    <div className="relative" ref={ref}>
      <button data-testid="header-location-chip" onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-2 rounded-full border border-[var(--b2-border)] bg-white px-3.5 py-2 text-sm font-medium text-[var(--b2-navy)] hover:border-[var(--b2-green)] transition-colors ${compact ? "w-full justify-center" : ""}`}>
        <MapPin className="w-4 h-4 text-[var(--b2-green)]" /> {label} <ChevronDown className="w-3.5 h-3.5 text-[#94A3B8]" />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-64 rounded-2xl bg-white border border-[var(--b2-border)] shadow-[0_16px_40px_rgba(13,27,61,0.12)] p-4 z-50" data-testid="location-popover">
          <p className="text-sm font-semibold text-[var(--b2-navy)] mb-2">Your area</p>
          <form onSubmit={apply} className="flex gap-2">
            <input data-testid="location-zip-input" value={zip} onChange={(e) => setZip(e.target.value)} inputMode="numeric"
              placeholder="ZIP code (e.g. 60586)" className="flex-1 rounded-xl border border-[var(--b2-border)] px-3 h-10 text-sm outline-none focus:border-[var(--b2-green)]" />
            <button type="submit" data-testid="location-apply" className="rounded-xl bg-[var(--b2-green)] hover:bg-[var(--b2-green-hover)] text-white px-3 h-10 text-sm font-semibold transition-colors">Set</button>
          </form>
          {loc?.zip && (
            <button data-testid="location-search" onClick={() => { setOpen(false); navigate(`/search?zip=${loc.zip}`); }}
              className="mt-3 w-full inline-flex items-center justify-center gap-2 text-sm font-semibold text-[var(--b2-green)]">
              <Search className="w-4 h-4" /> Nearby providers in {loc.city || `ZIP ${loc.zip}`}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export const PublicHeader = ({ rounded = false }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAppAuth();
  const isActive = (to) => pathname === to || (to !== "/" && pathname.startsWith(to));

  return (
    <header data-testid="public-header" className={`${rounded ? "relative rounded-t-[20px] sm:rounded-t-[28px] lg:rounded-t-[32px]" : "sticky top-0"} z-50 bg-white border-b border-[var(--b2-border)]`}>
      <div className="max-w-[1200px] mx-auto px-5 md:px-10 h-16 md:h-[76px] flex items-center justify-between gap-4">
        <button onClick={() => navigate("/")} data-testid="header-logo" className="shrink-0 flex items-center">
          <img
            src="/brand/logo-mobile.png"
            alt="Brivio"
            className="lg:hidden h-[31px] w-auto max-w-[130px] object-contain select-none"
            draggable="false"
            data-testid="header-logo-mobile"
          />
          <img
            src="/brand/logo-header.png"
            alt="Brivio"
            className="hidden lg:block h-[38px] w-auto max-w-[150px] object-contain select-none"
            draggable="false"
            data-testid="header-logo-desktop"
          />
        </button>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((l) => (
            <button key={l.to} data-testid={`nav-${l.label.replace(/ /g, "-").toLowerCase()}`} onClick={() => navigate(l.to)}
              className={`relative text-[15px] font-medium transition-colors ${isActive(l.to) ? "text-[var(--b2-green)]" : "text-[#475569] hover:text-[var(--b2-navy)]"}`}>
              {l.label}
              {isActive(l.to) && <span className="absolute -bottom-[27px] left-0 right-0 h-0.5 bg-[var(--b2-green)] rounded-full" />}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2.5">
          <LocationPicker />
          {user ? (
            <button data-testid="header-openapp" onClick={() => navigate("/app")}
              className="rounded-full border border-[var(--b2-border)] px-5 py-2 text-sm font-semibold text-[var(--b2-navy)] hover:border-[var(--b2-green)] hover:text-[var(--b2-green)] transition-colors">Open App</button>
          ) : (
            <button data-testid="header-login" onClick={() => navigate("/app/auth")}
              className="rounded-full border border-[var(--b2-border)] px-5 py-2 text-sm font-semibold text-[var(--b2-navy)] hover:border-[var(--b2-green)] hover:text-[var(--b2-green)] transition-colors">Log In</button>
          )}
          <button data-testid="header-join" data-cta="header-post-request" onClick={() => navigate("/b2/request")}
            className="rounded-full bg-[var(--b2-green)] text-white hover:bg-[var(--b2-green-hover)] px-5 py-2.5 text-sm font-semibold transition-all duration-200 whitespace-nowrap shadow-[0_6px_18px_rgba(22,163,74,0.3)] hover:-translate-y-0.5">Get Free Quotes</button>
        </div>

        <button className="lg:hidden text-[var(--b2-navy)]" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-[var(--b2-border)] px-5 py-4 flex flex-col gap-1" data-testid="mobile-drawer">
          {NAV.map((l) => (
            <button key={l.to} data-testid={`mobile-nav-${l.label.replace(/ /g, "-").toLowerCase()}`}
              onClick={() => { setOpen(false); navigate(l.to); }}
              className={`text-left py-2.5 font-medium ${isActive(l.to) ? "text-[var(--b2-green)]" : "text-[#475569]"}`}>{l.label}</button>
          ))}
          <div className="pt-2"><LocationPicker compact /></div>
          <div className="flex flex-col gap-2.5 pt-3">
            {user ? (
              <button data-testid="mobile-openapp" onClick={() => { setOpen(false); navigate("/app"); }}
                className="rounded-full border border-[var(--b2-border)] py-2.5 text-sm font-semibold text-[var(--b2-navy)]">Open App</button>
            ) : (
              <button data-testid="mobile-login" onClick={() => { setOpen(false); navigate("/app/auth"); }}
                className="rounded-full border border-[var(--b2-border)] py-2.5 text-sm font-semibold text-[var(--b2-navy)]">Log In</button>
            )}
            <button data-testid="mobile-join" data-cta="header-post-request" onClick={() => { setOpen(false); navigate("/b2/request"); }}
              className="rounded-full bg-[var(--b2-green)] text-white py-2.5 text-sm font-semibold">Get Free Local Quotes</button>
          </div>
        </div>
      )}
    </header>
  );
};
